import React, { useState } from 'react';
import { PlusCircle, Loader2, AlertCircle, Shield, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import FileUpload from './FileUpload';
import PackageSelection from './PackageSelection';
import { analysisAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CreateAnalysis = ({ onAnalysisCreated, onCancel }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: form, 2: package selection
  const [analysisId, setAnalysisId] = useState(null);
  const [formData, setFormData] = useState({
    resumeFile: null,
    jobDescriptionText: '',
    jobDescriptionFile: null,
    consentGiven: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.resumeFile) {
      setError('Please upload a resume file.');
      setLoading(false);
      return;
    }

    if (!formData.jobDescriptionText.trim()) {
      setError('Please provide a job description.');
      setLoading(false);
      return;
    }

    if (!formData.consentGiven) {
      setError('You must consent to processing to proceed.');
      setLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('resume_file', formData.resumeFile);
      submitData.append('job_description_text', formData.jobDescriptionText);
      submitData.append('package_type', 'quick_check'); // Default package for creation
      submitData.append('consent_given', formData.consentGiven);
      
      if (formData.jobDescriptionFile) {
        submitData.append('job_description_file', formData.jobDescriptionFile);
      }

      const result = await analysisAPI.createAnalysis(submitData);
      setAnalysisId(result.id);
      setStep(2); // Move to package selection
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelected = () => {
    // Analysis will be processed after payment, redirect to dashboard
    onAnalysisCreated({ id: analysisId });
  };

  const canCreateAnalysis = user?.can_create_analysis;
  const analysesToday = user?.analyses_today || 0;
  const maxAnalyses = user?.max_analyses_per_day || 5;

  if (!canCreateAnalysis) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="h-6 w-6 text-orange-500" />
            <span>Daily Limit Reached</span>
          </CardTitle>
          <CardDescription>
            You have reached your daily analysis limit of {maxAnalyses} analyses. 
            You have used {analysesToday} out of {maxAnalyses} analyses today.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-orange-800">
              Your daily limit will reset at midnight. You can create more analyses tomorrow.
            </p>
          </div>
          <Button variant="outline" onClick={onCancel} className="w-full">
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show package selection step
  if (step === 2 && analysisId) {
    return (
      <PackageSelection
        analysisId={analysisId}
        onPackageSelected={handlePackageSelected}
        onCancel={onCancel}
      />
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <PlusCircle className="h-6 w-6 text-blue-600" />
          <span>Create New Analysis</span>
        </CardTitle>
        <CardDescription>
          Upload your resume and job description to get AI-powered analysis and recommendations.
          ({analysesToday}/{maxAnalyses} analyses used today)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Resume Upload */}
          <FileUpload
            label="Resume"
            description="Upload your resume (PDF or DOCX)"
            onFileSelect={(file) => setFormData(prev => ({ ...prev, resumeFile: file }))}
            required
          />

          {/* Job Description Text */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Job Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Paste the job description here..."
              value={formData.jobDescriptionText}
              onChange={(e) => setFormData(prev => ({ ...prev, jobDescriptionText: e.target.value }))}
              className="min-h-[200px]"
              required
            />
            <p className="text-xs text-gray-500">
              Copy and paste the complete job description from the job posting.
            </p>
          </div>

          {/* Optional Job Description File */}
          <FileUpload
            label="Job Description File (Optional)"
            description="Upload job description file if available"
            onFileSelect={(file) => setFormData(prev => ({ ...prev, jobDescriptionFile: file }))}
          />

          {/* Consent Checkbox */}
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <h4 className="font-medium mb-2">Data Processing Consent</h4>
                  <p className="mb-3">
                    By proceeding, you consent to processing your resume and job posting to generate 
                    a detailed analysis report. Your data will be:
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li>• Processed by our AI system for analysis purposes only</li>
                    <li>• Automatically deleted after 30 days</li>
                    <li>• Never shared with third parties</li>
                    <li>• Deletable by you at any time from your dashboard</li>
                  </ul>
                </div>
              </div>
            </div>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.consentGiven}
                onChange={(e) => setFormData(prev => ({ ...prev, consentGiven: e.target.checked }))}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <span className="text-sm text-gray-700">
                <strong>I consent to processing my resume and job posting to generate a report.</strong> I understand 
                files are deleted after 30 days by default and I can delete them sooner at any time.
                <span className="text-red-500 ml-1">*</span>
              </span>
            </label>

            {!formData.consentGiven && (
              <p className="text-xs text-gray-500 ml-7">
                Consent is required to proceed with the analysis.
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h4>
            <div className="text-xs text-blue-700 space-y-1">
              <div className="flex items-center space-x-2">
                <span className="flex items-center justify-center w-4 h-4 bg-blue-600 text-white rounded-full text-xs font-bold">1</span>
                <span>Create analysis with your resume and job description</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="flex items-center justify-center w-4 h-4 bg-gray-400 text-white rounded-full text-xs font-bold">2</span>
                <span>Choose your analysis package (Quick, Targeted, or Monthly)</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="flex items-center justify-center w-4 h-4 bg-gray-400 text-white rounded-full text-xs font-bold">3</span>
                <span>Complete payment and receive AI-powered insights</span>
              </div>
            </div>
          </div>

          <div className="flex space-x-4">
            <Button
              type="submit"
              disabled={loading || !formData.consentGiven}
              className="flex-1 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Creating Analysis...</span>
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4" />
                  <span>Continue to Package Selection</span>
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateAnalysis;